/* patch.js — Langue d'une piste réécrite sur place dans un ISO BMFF (MP4, MOV,
 * M4A) : l'en-tête « mdhd », sans déplacer un octet. scan() situe, plan()
 * traduit, parts() décrit le fichier corrigé en tranches — rien n'est chargé en
 * mémoire. read(offset, length) -> Promise<Uint8Array>. */
'use strict';

var CONTAINERS = { 'MPEG-4': 1, 'QuickTime': 1 }; // model.container.formatRaw
// boîtes à traverser
var PARENTS = { moov: 1 }; // seul niveau traversé d'office : les pistes sont lues à la main
var HANDLERS = { vide: 'video', soun: 'audio', sbtl: 'text', text: 'text', subp: 'text', clcp: 'text' };

var LABELS = { language: 'Langue' };

var REASONS = {
  container: 'La correction dans le navigateur ne connaît que les fichiers MP4, MOV et M4A.',
  noMoov: 'L’index (moov) de ce fichier est introuvable.',
  moovBig: 'L’index (moov) de ce fichier est trop volumineux pour être corrigé ici.',
  noTrack: 'La piste visée n’a pas été retrouvée dans le fichier.',
  noMdhd: 'L’en-tête de cette piste (mdhd) est introuvable.',
  elng: 'Cette piste déclare aussi sa langue au format étendu (elng), que cette correction ne sait pas réécrire.',
  lang: 'Le code de langue doit avoir trois lettres.',
  type: 'Cette correction ne peut pas se faire dans le navigateur.',
  truncated: 'Le fichier est incomplet : il n’est pas corrigé ici.'
};

function u32(b, o) { return ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0; }
function u64(b, o) { return u32(b, o) * 4294967296 + u32(b, o + 4); }
function type(b, o) { return String.fromCharCode(b[o], b[o + 1], b[o + 2], b[o + 3]); }

/* ---------- Parcours des boîtes ---------- */

// Taille 0 vaut « jusqu'à la fin » ; `rest` dit ce qui reste.
function header(buf, o, rest) {
  if (o + 8 > buf.length) return null;
  var size = u32(buf, o), head = 8;
  if (size === 1) {
    if (o + 16 > buf.length) return null;
    size = u64(buf, o + 8); head = 16;
  } else if (size === 0) size = rest;
  if (size < head || size > rest) return null;
  return { size: size, type: type(buf, o + 4), head: head };
}

// moov au premier niveau, sans lire les données.
function findMoov(read, size) {
  var offset = 0;
  function step() {
    if (offset + 8 > size) return Promise.resolve(null);
    return read(offset, 16).then(function (b) {
      var h = header(b, 0, size - offset);
      if (!h) return null;
      if (h.type === 'moov') return { offset: offset, size: h.size, head: h.head };
      offset += h.size;
      return step();
    });
  }
  return step();
}

var MAX_MOOV = 128 * 1024 * 1024;

// -> { ok, reason } ou { ok: true, tracks: [...] }
function scan(read, size, containerRaw) {
  if (containerRaw && !CONTAINERS[containerRaw]) return Promise.resolve({ ok: false, reason: REASONS.container });
  return findMoov(read, size).then(function (moov) {
    if (!moov) return { ok: false, reason: REASONS.noMoov };
    if (moov.size > MAX_MOOV) return { ok: false, reason: REASONS.moovBig };
    return read(moov.offset, moov.size).then(function (buf) {
      return { ok: true, tracks: readTracks(buf, moov.offset) };
    });
  }, function () { return { ok: false, reason: REASONS.noMoov }; });
}

/* Type et langue se lisent sous « mdia » seulement : le « hdlr » de « minf »
 * décrit la source des données, pas la piste. */
function readTracks(buf, base) {
  var tracks = [];
  var counts = {};
  walk(buf, 0, buf.length, function (box) {
    if (box.type !== 'trak') return true;
    var t = { kind: null, langOffset: null, langVersion: null, language: null, elng: false };
    children(buf, box, function (b) {
      if (b.type !== 'mdia') return;
      children(buf, b, function (m) {
        var body = m.start + m.head;
        if (m.type === 'hdlr') t.kind = HANDLERS[type(buf, body + 8)] || null;
        else if (m.type === 'mdhd') {
          var v = buf[body];
          // version/flags (4) + création, modification, échelle, durée
          var skip = v === 1 ? 4 + 8 + 8 + 4 + 8 : 4 + 4 + 4 + 4 + 4;
          t.langVersion = v;
          t.langOffset = base + body + skip;
          t.language = unpackLanguage(buf, body + skip);
        } else if (m.type === 'elng') t.elng = true;
      });
    });
    if (t.kind) {
      counts[t.kind] = (counts[t.kind] || 0) + 1;
      t.ordinal = counts[t.kind];
      tracks.push(t);
    }
    return false;
  });
  return tracks;
}

function children(buf, box, fn) {
  var o = box.start + box.head, end = box.start + box.size;
  while (o + 8 <= end) {
    var h = header(buf, o, end - o);
    if (!h) return;
    fn({ type: h.type, start: o, size: h.size, head: h.head });
    o += h.size;
  }
}

// fn renvoie false pour ne pas descendre.
function walk(buf, start, end, fn) {
  var o = start;
  while (o + 8 <= end) {
    var h = header(buf, o, end - o);
    if (!h) return;
    var descend = fn({ type: h.type, start: o, size: h.size, head: h.head });
    if (descend !== false && PARENTS[h.type]) walk(buf, o + h.head, o + h.size, fn);
    o += h.size;
  }
}

/* ---------- Langue ---------- */

// ISO 639-2/T sur 15 bits : trois lettres, chacune diminuée de 0x60.
function packLanguage(code) {
  if (!/^[a-z]{3}$/.test(String(code || '').toLowerCase())) return null;
  var c = String(code).toLowerCase();
  var v = 0;
  for (var i = 0; i < 3; i++) v = (v << 5) | ((c.charCodeAt(i) - 0x60) & 0x1f);
  return new Uint8Array([(v >> 8) & 0x7f, v & 0xff]);
}

function unpackLanguage(buf, o) {
  var v = (buf[o] << 8) | buf[o + 1];
  var out = '';
  for (var i = 2; i >= 0; i--) out += String.fromCharCode(((v >> (i * 5)) & 0x1f) + 0x60);
  return /^[a-z]{3}$/.test(out) ? out : null;
}

/* ---------- Traduction des actions en retouches ---------- */

function trackOf(scanned, target) {
  for (var i = 0; i < scanned.tracks.length; i++) {
    var t = scanned.tracks[i];
    if (t.kind === target.kind && t.ordinal === (target.ordinal || 1)) return t;
  }
  return null;
}

function supports(action) { return action.type === 'set-language'; }

// -> { patches: [{offset, bytes}], changes, rejected: [{action, reason}] }
function plan(actions, scanned) {
  var patches = [], rejected = [];
  actions.forEach(function (a) {
    if (!supports(a)) return rejected.push({ action: a, reason: REASONS.type });
    var t = trackOf(scanned, a.target || {});
    if (!t) return rejected.push({ action: a, reason: REASONS.noTrack });
    if (t.langOffset == null) return rejected.push({ action: a, reason: REASONS.noMdhd });
    if (t.elng) return rejected.push({ action: a, reason: REASONS.elng });
    var bytes = packLanguage(a.params.language);
    if (!bytes) return rejected.push({ action: a, reason: REASONS.lang });
    patches.push({ offset: t.langOffset, bytes: bytes, before: t.language, after: String(a.params.language).toLowerCase() });
  });
  patches.sort(function (x, y) { return x.offset - y.offset; });
  var changes = patches.map(function (p) { return { label: LABELS.language, before: p.before, after: p.after }; });
  return { patches: patches, changes: changes, rejected: rejected };
}

// Tranches de l'original ({from, to}) et octets réécrits ({bytes}).
function parts(patches, size) {
  var out = [], at = 0;
  patches.forEach(function (p) {
    if (p.offset > at) out.push({ from: at, to: p.offset });
    out.push({ bytes: p.bytes });
    at = p.offset + p.bytes.length;
  });
  if (at < size) out.push({ from: at, to: size });
  return out;
}

var api = {
  REASONS: REASONS, CONTAINERS: CONTAINERS, LABELS: LABELS,
  scan: scan, plan: plan, parts: parts, supports: supports,
  packLanguage: packLanguage, unpackLanguage: unpackLanguage
};
globalThis.MediaPatch = api;
export default api;
